import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import SettingsIcon from '../../public/Settings.svg';
import { Box, Button, Chip, CircularProgress, Dialog, Divider, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { UserAPI } from '../apis/UserAPI';
import { fetchSupportedLanguagesData, buildFallbackLanguageOptions, formatPreferredName, fetchEnabledMessageChannels, buildDefaultMessagePreferences } from '../utils/settingsModalHelpers';
import { constants } from '../constants';
import { handleErrorModal } from './Toast';
import { SETTINGS_VALIDATION } from '@/utils';
import { ConfigContext } from './ConfigContext';
import { useRbacNotifications } from '@/hooks/useRbacNotifications';
import { useThemeDetails } from './ThemeDetailsContext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { checkProfanity } from '@/utils/profanityCheckUtils';
import { defaultCountries, parseCountry } from 'react-international-phone';
import { parsePhoneNumber } from 'libphonenumber-js/mobile';
import PhoneInput from './PhoneInput';
import { validatePhoneNumber as validatePhoneUtil } from '../utils/phoneValidation';
const getChannelLabelFromConstants = (channelKey, fallback) => {
    switch (channelKey) {
        case 'email':
            return constants.settings_message_email;
        case 'sms':
            return constants.settings_message_sms;
        case 'slack':
            return constants.settings_message_slack;
        case 'teams':
            return constants.settings_message_teams;
        case 'webPushNotification':
            return constants.settings_message_web_push_notification;
        default:
            return fallback;
    }
};
// Helper function to normalize notification permission state
const normalizePermissionState = (state) => {
    return state === 'prompt' ? 'default' : state;
};
// Helper function to validate cleaned preferred name
const isValidCleanedName = (cleanedName) => {
    return (cleanedName.length > 0 &&
        cleanedName.length <= SETTINGS_VALIDATION.maxNameLength);
};
// Helper function to check if user has valid ID
const hasValidUserId = (user) => {
    return Boolean(user && user.id);
};
const getPushEnableFailureMessage = (reason) => {
    if (reason === 'permission_denied') {
        return constants.settings_push_permission_denied;
    }
    if (reason === 'no_vapid_key') {
        return constants.settings_push_not_configured;
    }
    return constants.settings_push_subscription_failed;
};
// Helper function to apply web push notification preference.
// Returns an error string on failure, null on success.
const applyWebPushNotification = async (newSettings, isUserSubscribed, notificationPermission, enablePushNotification, disablePushNotification, refreshNotificationPermission) => {
    const desiredPush = Boolean(newSettings.messagePreferences.webPushNotification);
    const currentPush = Boolean(isUserSubscribed);
    if (desiredPush === currentPush)
        return null;
    if (!desiredPush) {
        await disablePushNotification();
        newSettings.messagePreferences.webPushNotification = false;
        return null;
    }
    if (desiredPush && notificationPermission === 'denied') {
        return constants.settings_push_permission_denied;
    }
    const result = await enablePushNotification();
    await refreshNotificationPermission();
    if (Notification.permission === 'denied') {
        return constants.settings_push_permission_denied;
    }
    if (!result.ok) {
        return getPushEnableFailureMessage(result.reason);
    }
    newSettings.messagePreferences.webPushNotification = true;
    return null;
};
// Helper function to fetch supported languages
const fetchLanguageOptions = async (currentSelected, fallbackSelected) => {
    try {
        const { options, selected } = await fetchSupportedLanguagesData(currentSelected, fallbackSelected);
        return { options, selected, error: null };
    }
    catch {
        const { options, selected } = buildFallbackLanguageOptions(currentSelected, fallbackSelected);
        return { options, selected, error: constants.settings_language_error };
    }
};
// Helper function to check user permissions
const checkUserPermissions = async (username, handleErrorModalFn) => {
    try {
        const policies = await UserAPI.getPolicies(username);
        const hasPermission = policies?.resource?.some((policy) => policy?.resource_action?.some((ra) => ra?.action?.includes('RBAC:UpdateUser'))) ?? false;
        if (!hasPermission) {
            handleErrorModalFn(true, constants.settings_permission_update_denied);
        }
        return hasPermission;
    }
    catch (error) {
        console.error('Failed to check permissions:', error);
        handleErrorModalFn(true, constants.settings_permissions_unverifiable);
        return false;
    }
};
// Helper function to load enabled message channels
const loadEnabledChannels = async () => {
    try {
        const channels = await fetchEnabledMessageChannels();
        return channels;
    }
    catch (error) {
        console.error('Failed to load message channels:', error);
        return [];
    }
};
// Helper function to render message type toggle
const renderMessageTypeToggle = (name, active, handleToggler, hasUpdatePermission, buttonColor, disabled) => {
    const interactive = hasUpdatePermission && !disabled;
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsxs(Box, { onClick: () => interactive && handleToggler(), sx: {
                    position: 'relative',
                    width: 48,
                    height: 32,
                    cursor: interactive ? 'pointer' : 'not-allowed',
                    opacity: interactive ? 1 : 0.5
                }, children: [_jsx(Box, { sx: {
                            position: 'absolute',
                            left: 6,
                            top: 8,
                            width: 36,
                            height: 16,
                            borderRadius: '10px',
                            backgroundColor: active ? buttonColor : '#EBEBEB',
                            opacity: active ? 0.3 : 1,
                            transition: 'background-color 300ms',
                            pointerEvents: 'none'
                        } }), _jsx(Box, { component: "button", role: "switch", "aria-checked": active, "aria-label": 'Enable ' + name, sx: {
                            position: 'absolute',
                            left: active ? 24 : 7,
                            top: 7,
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            backgroundColor: active ? buttonColor : '#D9D9D9',
                            boxShadow: '0px 2px 1px rgba(0, 0, 0, 0.15), 0px 0px 2px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.15)',
                            transition: 'left 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 300ms',
                            cursor: 'pointer',
                            border: 'none',
                            padding: 0,
                            pointerEvents: 'none',
                            '&:focus': {
                                outline: `2px solid ${COLORS.primary}`,
                                outlineOffset: '2px'
                            }
                        } })] }), _jsx(Box, { component: "span", sx: descriptionStyle, children: name })] }));
};
const COLORS = {
    primary: '#26890D',
    primaryHover: '#1f6b0a',
    primaryLight: 'rgba(38, 137, 13, 0.04)',
    textPrimary: '#212121',
    textSecondary: '#666',
    border: '#9E9E9E',
    divider: '#EBEBEB',
    background: '#FFF',
    actionButtonsBackground: '#F8F8F8',
    toggleOff: '#FAFAFA',
    error: '#d32f2f',
    buttonBackground: '#F8F8F8'
};
const headingStyle = {
    color: COLORS.textPrimary,
    fontFamily: 'Open Sans',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '152%',
    letterSpacing: '0.15px',
    margin: 0,
    '@media (max-width: 768px)': { fontSize: '15px' }
};
const descriptionStyle = {
    color: COLORS.textSecondary,
    fontFamily: 'Open Sans',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '152%',
    letterSpacing: '0.15px',
    margin: 0,
    '@media (max-width: 768px)': { fontSize: '14px' }
};
const inputBaseStyle = {
    fontFamily: 'Open Sans',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '152%',
    letterSpacing: '0.15px',
    color: COLORS.textPrimary
};
const getNotificationEnableSteps = () => {
    const NOTIFICATION_DISABLED_PREFIX = constants.notification_disabled_prefix;
    const generic = constants.notification_enable_generic;
    if (typeof navigator === 'undefined') {
        return `${NOTIFICATION_DISABLED_PREFIX} ${generic}`;
    }
    const ua = navigator.userAgent.toLowerCase();
    const rules = [
        {
            test: (u) => u.includes('edg/'),
            instruction: constants.notification_enable_edge
        },
        {
            test: (u) => (u.includes('chrome') || u.includes('chromium')) &&
                !u.includes('edg/'),
            instruction: constants.notification_enable_chrome
        },
        {
            test: (u) => u.includes('firefox'),
            instruction: constants.notification_enable_firefox
        },
        {
            test: (u) => u.includes('safari') &&
                !u.includes('chrome') &&
                !u.includes('chromium'),
            instruction: constants.notification_enable_safari
        }
    ];
    const matched = rules.find((r) => r.test(ua));
    return `${NOTIFICATION_DISABLED_PREFIX} ${matched?.instruction ?? generic}`;
};
// Helper: Build message preferences from user data and enabled channels
const buildMessagePreferences = (enabledChannels, initialUser) => {
    const derivedMessagePreferences = buildDefaultMessagePreferences(enabledChannels);
    for (const channel of enabledChannels) {
        const channelKey = channel.key;
        derivedMessagePreferences[channelKey] = Boolean(initialUser?.details?.messagePreferences?.[channelKey] ??
            initialUser?.details?.[channelKey] ??
            false);
    }
    return derivedMessagePreferences;
};
const findCountryByDialCode = (dialCode) => {
    for (const country of defaultCountries) {
        const parsed = parseCountry(country);
        if (parsed.dialCode === dialCode) {
            return parsed.iso2;
        }
    }
    return 'us';
};
const parseLegacyPhoneNumber = (phoneData) => {
    if (!phoneData.startsWith('+'))
        return null;
    try {
        const parsed = parsePhoneNumber(phoneData);
        if (parsed) {
            return {
                country: parsed.country?.toLowerCase() || 'us',
                number: parsed.nationalNumber
            };
        }
    }
    catch (error) {
        console.warn('Failed to parse phone number:', error);
    }
    return null;
};
// Helper: Parse JSON phone data
const parseJsonPhoneData = (phoneData) => {
    try {
        const parsed = JSON.parse(phoneData);
        // New format with country and number
        if (parsed?.country && parsed?.number) {
            return {
                country: parsed.country.toLowerCase(),
                number: parsed.number
            };
        }
        // Legacy format with dialCode and number
        if (parsed?.dialCode && parsed?.number) {
            return {
                country: findCountryByDialCode(parsed.dialCode),
                number: parsed.number
            };
        }
    }
    catch (e) {
        console.error('Failed to parse phone data as JSON:', e);
    }
    return null;
};
// Helper: Parse phone number from JSON or legacy string format
const parsePhoneNumberWithCountry = (phoneData) => {
    if (!phoneData) {
        return { country: 'us', number: '' };
    }
    const jsonResult = parseJsonPhoneData(phoneData);
    if (jsonResult)
        return jsonResult;
    // Try legacy format (full phone number with + prefix)
    const legacyResult = parseLegacyPhoneNumber(phoneData);
    if (legacyResult)
        return legacyResult;
    return { country: 'us', number: '' };
};
const initializeSettingsFromUser = (enabledChannels, initialUser) => {
    const cookiePreferredName = initialUser?.userAttributes?.name?.trim() || '';
    const cookieLang = initialUser?.details?.selected_language?.trim()?.toLowerCase() || 'en';
    const cookieAudio = initialUser?.details?.audio ?? false;
    const cookiePhoneNumber = initialUser?.userAttributes?.phone || '';
    const messagePreferences = buildMessagePreferences(enabledChannels, initialUser);
    return {
        preferredName: cookiePreferredName,
        phoneNumber: cookiePhoneNumber,
        selectedLanguage: cookieLang,
        audio: cookieAudio,
        messagePreferences
    };
};
// Helper: Update user cookie with new data
const updateUserCookie = (updatedUser, newSettings) => {
    const patchedUser = {
        ...updatedUser,
        userAttributes: {
            ...updatedUser.userAttributes,
            phone: newSettings.phoneNumber
        },
        details: {
            ...updatedUser.details,
            messagePreferences: {
                ...updatedUser.details?.messagePreferences,
                ...newSettings.messagePreferences
            }
        }
    };
    try {
        Cookies.remove('user', { path: '/' });
        Cookies.set('user', JSON.stringify(patchedUser), {
            secure: true,
            sameSite: 'Strict',
            path: '/'
        });
    }
    catch (e) {
        console.warn('[SettingsModal] failed to refresh user cookie', e);
    }
};
// Notification warning styles
const notificationWarningStyles = {
    container: {
        mt: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        px: '16px',
        py: '10px'
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0
    },
    icon: { fontSize: '20px', flexShrink: 0 },
    text: {
        fontFamily: 'Open Sans',
        fontSize: '14px',
        lineHeight: '20px',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere'
    }
};
const ChannelToggleItem = ({ channel, messagePreferences, notificationPermission, renderChannelToggle, toggleInBrowser, toggleMessagePreference, webPushError }) => {
    const isWebPush = channel.key === 'webPushNotification';
    const isActive = Boolean(messagePreferences[channel.key]);
    // Show error whenever a push error exists (toggle may be OFF after auto-revert)
    const showError = isWebPush && !!webPushError;
    // Proactive hint when permission is denied, toggle is OFF, and no error yet shown
    const showWarning = isWebPush && notificationPermission === 'denied' && !isActive && !webPushError;
    // Disable the toggle only when it is OFF with an error (e.g. permission denied
    // and the effect already reverted it). When the toggle is ON the user must still
    // be able to click it again to turn it off.
    const isDisabled = isWebPush && !!webPushError && !isActive;
    const handleToggle = isWebPush
        ? toggleInBrowser
        : () => toggleMessagePreference(channel.key);
    return (_jsxs(Box, { children: [renderChannelToggle(channel.label, isActive, handleToggle, isDisabled), showError && (_jsx(Box, { sx: notificationWarningStyles.container, children: _jsxs(Box, { sx: notificationWarningStyles.content, children: [_jsx(ErrorOutlineIcon, { sx: {
                                ...notificationWarningStyles.icon,
                                color: '#D32F2F'
                            } }), _jsx(Typography, { sx: {
                                ...notificationWarningStyles.text,
                                color: '#D32F2F'
                            }, children: webPushError })] }) })), showWarning && (_jsx(Box, { sx: notificationWarningStyles.container, children: _jsxs(Box, { sx: notificationWarningStyles.content, children: [_jsx(ErrorOutlineIcon, { sx: notificationWarningStyles.icon }), _jsx(Typography, { sx: notificationWarningStyles.text, children: getNotificationEnableSteps() })] }) }))] }));
};
// Default settings state
const DEFAULT_SETTINGS = {
    preferredName: '',
    phoneNumber: '',
    selectedLanguage: 'en',
    audio: false,
    messagePreferences: {}
};
// Loading view component to reduce cognitive complexity
const SettingsLoadingView = ({ buttonColor }) => {
    const { stateImages } = useThemeDetails();
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            minHeight: '300px',
            gap: '16px',
            '@media (max-width: 768px)': {
                padding: '16px',
                gap: '12px'
            }
        }, "aria-label": "Loading settings", children: [stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, { size: 64, thickness: 4, sx: { color: buttonColor } })), _jsx(Box, { component: "p", sx: {
                    fontFamily: 'Open Sans',
                    fontWeight: 600,
                    fontSize: '20px',
                    lineHeight: '152%',
                    letterSpacing: '0.15px',
                    textAlign: 'center',
                    color: COLORS.textPrimary,
                    margin: 0
                }, children: constants.loading })] }));
};
const SettingsFooter = ({ onClose, handleSave, languageLoading, preferredNameError, phoneNumberError, isDirty, hasUpdatePermission, buttonColor, isSaving = false, hasWebPushError = false }) => {
    const isSaveDisabled = !!preferredNameError ||
        !!phoneNumberError ||
        !isDirty ||
        !hasUpdatePermission ||
        isSaving ||
        hasWebPushError;
    return (_jsxs(Box, { sx: {
            display: 'flex',
            padding: '16px 24px',
            gap: '16px',
            borderRadius: '0 0 24px 24px',
            background: COLORS.actionButtonsBackground,
            borderTop: `1px solid ${COLORS.divider}`,
            flexShrink: 0,
            '@media (max-width: 768px)': {
                padding: '12px 16px',
                gap: '12px'
            }
        }, children: [_jsx(Button, { onClick: onClose, sx: {
                    padding: '8px 16px',
                    borderRadius: '4px',
                    color: buttonColor,
                    fontFamily: 'Open Sans',
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '168%',
                    letterSpacing: '0.4px',
                    textTransform: 'none',
                    '@media (max-width: 768px)': {
                        fontSize: '15px',
                        padding: '8px 12px'
                    }
                }, children: constants.settings_button_cancel }), !languageLoading && (_jsx(Box, { sx: {
                    flex: '1 0 0',
                    display: 'flex',
                    justifyContent: 'flex-end'
                }, children: _jsx(Button, { onClick: handleSave, variant: "contained", disabled: isSaveDisabled, sx: {
                        padding: '8px 16px',
                        borderRadius: '4px',
                        backgroundColor: isSaveDisabled
                            ? '#9e9e9e'
                            : buttonColor,
                        color: '#FFF',
                        fontFamily: 'Open Sans',
                        fontSize: '16px',
                        fontWeight: 600,
                        lineHeight: '168%',
                        letterSpacing: '0.4px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                        '&.Mui-disabled': {
                            backgroundColor: '#9e9e9e',
                            color: '#FFF'
                        },
                        '@media (max-width: 768px)': {
                            fontSize: '15px',
                            padding: '8px 12px'
                        }
                    }, children: isSaving ? (_jsx(CircularProgress, { size: 16, sx: { color: '#FFF' } })) : (constants.settings_button_save) }) }))] }));
};
const SettingsModal = ({ open, onClose, onUpdate, initialUser }) => {
    // Core settings state
    const [originalSettings, setOriginalSettings] = useState(DEFAULT_SETTINGS);
    const [preferredName, setPreferredName] = useState('');
    const [preferredNameError, setPreferredNameError] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneNumberError, setPhoneNumberError] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState('us');
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [saveError, setSaveError] = useState(null);
    const [audio, setAudio] = useState(false);
    const [messagePreferences, setMessagePreferences] = useState({});
    const webPushEnabledRef = useRef(false);
    webPushEnabledRef.current = Boolean(messagePreferences.webPushNotification);
    // Channels state
    const [enabledChannels, setEnabledChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(true);
    // Language state
    const [languageOptions, setLanguageOptions] = useState([]);
    const [languageLoading, setLanguageLoading] = useState(false);
    const [languageError, setLanguageError] = useState(null);
    // Roles state
    const [userRoles, setUserRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    // Phone from DB — overrides stale cookie value once fetched
    const [dbPhone, setDbPhone] = useState(undefined);
    // Permissions state
    const [hasUpdatePermission, setHasUpdatePermission] = useState(true);
    const [notificationPermission, setNotificationPermission] = useState('default');
    const isSavingRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const [webPushError, setWebPushError] = useState(null);
    // Hooks
    const { isUserSubscribed, enablePushNotification, disablePushNotification, hasVapidKey } = useRbacNotifications();
    const { configState } = useContext(ConfigContext);
    const buttonColor = configState?.settings?.button_color ?? COLORS.primary;
    const refreshNotificationPermission = useCallback(async () => {
        if (!('Notification' in globalThis))
            return;
        try {
            if ('permissions' in navigator && navigator.permissions?.query) {
                const status = await navigator.permissions.query({
                    name: 'notifications'
                });
                setNotificationPermission(normalizePermissionState(status.state));
                status.onchange = () => {
                    setNotificationPermission(normalizePermissionState(status.state));
                };
                return;
            }
        }
        catch {
            // fall back below
        }
        setNotificationPermission(Notification.permission);
    }, []);
    useEffect(() => {
        if (!open)
            return;
        if (notificationPermission === 'granted') {
            // User granted permission from browser settings — clear any push error
            setWebPushError((prev) => prev === constants.settings_push_permission_denied ? null : prev);
        }
        else if (notificationPermission === 'denied' && webPushEnabledRef.current) {
            // Permission was revoked externally while the toggle was on — revert it.
            // webPushEnabledRef keeps the value current without adding it to the deps,
            // so this effect only fires on external permission changes, not on
            // user-initiated toggle clicks (which would cause an unwanted double-revert).
            setMessagePreferences((prev) => ({
                ...prev,
                webPushNotification: false
            }));
            disablePushNotification().catch(() => { });
        }
    }, [open, notificationPermission, disablePushNotification]);
    useEffect(() => {
        if (!open)
            return;
        refreshNotificationPermission();
        const onFocus = () => refreshNotificationPermission();
        const onVisibility = () => {
            if (document.visibilityState !== 'visible')
                return;
            refreshNotificationPermission();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [open, refreshNotificationPermission]);
    // Single effect to load all modal data in parallel on open.
    // Permissions, roles and channels are independent — run them concurrently.
    useEffect(() => {
        if (!open) {
            setDbPhone(undefined);
            return;
        }
        let cancelled = false;
        setLanguageLoading(true);
        setChannelsLoading(true);
        setRolesLoading(true);
        async function loadModalData() {
            const [channels] = await Promise.all([
                // 1. Load message channels (result needed by the settings initialiser)
                loadEnabledChannels(),
                // 2. Check update permission
                initialUser?.username
                    ? checkUserPermissions(initialUser.username, handleErrorModal).then((hasPermission) => {
                        if (!cancelled)
                            setHasUpdatePermission(hasPermission);
                    })
                    : Promise.resolve(),
                // 3. Load roles
                UserAPI.getRoles(initialUser?.username)
                    .then((response) => {
                    if (!cancelled)
                        setUserRoles(response?.resource || []);
                })
                    .catch((err) => {
                    console.error('[SettingsModal] Failed to load roles:', err);
                    if (!cancelled)
                        setUserRoles([]);
                })
                    .finally(() => {
                    if (!cancelled)
                        setRolesLoading(false);
                }),
                // 4. Fetch fresh user data to get the real phone from DB
                initialUser?.username
                    ? UserAPI.getUsers(initialUser.username)
                        .then((response) => {
                        // undefined means the field isn't set in DB — keep cookie value
                        const freshPhone = response?.resource?.[0]?.userAttributes?.phone;
                        if (!cancelled)
                            setDbPhone(freshPhone);
                    })
                        .catch(() => {
                        // Fall back to cookie value silently
                    })
                    : Promise.resolve()
            ]);
            if (cancelled)
                return;
            setEnabledChannels(channels);
            setChannelsLoading(false);
        }
        loadModalData();
        return () => {
            cancelled = true;
        };
    }, [open, initialUser?.username]);
    const initializeSettings = (derived) => {
        setOriginalSettings(derived);
        setPreferredName(derived.preferredName);
        // Parse phone number with country code
        const { country, number } = parsePhoneNumberWithCountry(derived.phoneNumber);
        setSelectedCountry(country);
        setPhoneNumber(number);
        setPhoneNumberError(null);
        setSelectedLanguage(derived.selectedLanguage);
        setAudio(derived.audio);
        setMessagePreferences(derived.messagePreferences);
        setPreferredNameError(null);
        setWebPushError(null);
        setSaveError(null);
        fetchSupportedLanguages(derived.selectedLanguage);
    };
    useEffect(() => {
        if (!open || channelsLoading)
            return;
        const derived = initializeSettingsFromUser(enabledChannels, initialUser);
        // Use DB phone if available — overrides stale cookie value
        if (dbPhone !== undefined) {
            derived.phoneNumber = dbPhone;
        }
        initializeSettings(derived);
    }, [open, initialUser, enabledChannels, channelsLoading, dbPhone]);
    async function fetchSupportedLanguages(currentSelected) {
        setLanguageLoading(true);
        const { options, selected, error } = await fetchLanguageOptions(currentSelected, selectedLanguage);
        setLanguageOptions(options);
        setSelectedLanguage(selected);
        setLanguageError(error);
        setLanguageLoading(false);
    }
    const validatePreferredName = useCallback((value) => {
        const collapsed = value.replaceAll(/\s+/g, ' ').trim();
        if (!collapsed)
            return constants.settings_preferred_name_required;
        if (!SETTINGS_VALIDATION.nameAllowedChars.test(collapsed))
            return constants.settings_preferred_name_invalid_chars;
        if (!SETTINGS_VALIDATION.nameHasLetter.test(collapsed))
            return constants.settings_preferred_name_no_letter;
        if (collapsed.length > SETTINGS_VALIDATION.maxNameLength)
            return constants.settings_preferred_name_max_length;
        if (checkProfanity(collapsed))
            return constants.settings_preferred_name_profanity_error;
        return null;
    }, []);
    const handlePreferredNameChange = useCallback((event) => {
        const value = event.target.value;
        setPreferredName(value);
        setPreferredNameError(validatePreferredName(value));
    }, [validatePreferredName]);
    const handlePhoneChange = useCallback((newValue, newCountry) => {
        const isSmsEnabled = Boolean(messagePreferences.sms);
        setPhoneNumber(newValue);
        setSelectedCountry(newCountry);
        setPhoneNumberError(validatePhoneUtil(newValue, newCountry, isSmsEnabled));
    }, [messagePreferences.sms]);
    const isDirty = useMemo(() => {
        const { country: origCountry, number: origNumber } = parsePhoneNumberWithCountry(originalSettings.phoneNumber);
        const basicFieldsChanged = preferredName !== originalSettings.preferredName ||
            phoneNumber !== origNumber ||
            selectedCountry !== origCountry ||
            selectedLanguage !== originalSettings.selectedLanguage ||
            audio !== originalSettings.audio;
        const preferencesChanged = enabledChannels.some((ch) => Boolean(messagePreferences[ch.key]) !==
            Boolean(originalSettings.messagePreferences?.[ch.key]));
        return basicFieldsChanged || preferencesChanged;
    }, [
        preferredName,
        phoneNumber,
        selectedCountry,
        selectedLanguage,
        audio,
        originalSettings,
        messagePreferences,
        enabledChannels
    ]);
    const applyWebPushPreference = useCallback(async (newSettings) => {
        return applyWebPushNotification(newSettings, isUserSubscribed, notificationPermission, enablePushNotification, disablePushNotification, refreshNotificationPermission);
    }, [
        isUserSubscribed,
        notificationPermission,
        enablePushNotification,
        disablePushNotification,
        refreshNotificationPermission
    ]);
    const validateAndGetCleanedName = useCallback(() => {
        if (preferredNameError)
            return null;
        const cleanedName = formatPreferredName(preferredName);
        if (!isValidCleanedName(cleanedName)) {
            setPreferredNameError(constants.settings_preferred_name_max_length);
            return null;
        }
        return cleanedName;
    }, [preferredName, preferredNameError]);
    const buildNewSettings = useCallback((cleanedName) => {
        const country = defaultCountries.find((c) => c[1] === selectedCountry);
        const dialCode = country ? parseCountry(country).dialCode : '1';
        return {
            preferredName: cleanedName,
            phoneNumber: phoneNumber ? `+${dialCode}${phoneNumber}` : '',
            selectedLanguage,
            audio,
            messagePreferences: { ...messagePreferences }
        };
    }, [
        phoneNumber,
        selectedCountry,
        selectedLanguage,
        audio,
        messagePreferences
    ]);
    const finalizeSettings = useCallback((newSettings, updatedUser) => {
        onUpdate(newSettings, updatedUser);
        setOriginalSettings(newSettings);
        onClose();
        setTimeout(() => globalThis?.location?.reload(), SETTINGS_VALIDATION.reloadDelay);
    }, [onUpdate, onClose]);
    const saveUserPreferences = useCallback(async (newSettings) => {
        try {
            const response = await UserAPI.updateUserPreferences(initialUser, {
                preferredName: newSettings.preferredName,
                ...(newSettings.phoneNumber ? { phone: newSettings.phoneNumber } : {}),
                selectedLanguage: newSettings.selectedLanguage,
                audio: newSettings.audio,
                messagePreferences: newSettings.messagePreferences
            });
            const updatedUser = response?.resource?.[0];
            if (updatedUser)
                updateUserCookie(updatedUser, newSettings);
            finalizeSettings(newSettings, updatedUser);
        }
        catch (e) {
            console.warn('[SettingsModal] preference update failed', e);
            const msg = e instanceof Error
                ? e.message
                : constants.settings_save_error;
            setSaveError(msg || constants.settings_save_error);
        }
    }, [initialUser, finalizeSettings]);
    const handleSave = useCallback(async () => {
        if (isSavingRef.current)
            return;
        isSavingRef.current = true;
        setIsSaving(true);
        setSaveError(null);
        try {
            if (phoneNumberError)
                return;
            const cleanedName = validateAndGetCleanedName();
            if (!cleanedName)
                return;
            const newSettings = buildNewSettings(cleanedName);
            const hasWebPush = enabledChannels.some((c) => c.key === 'webPushNotification');
            if (hasWebPush) {
                const pushError = await applyWebPushPreference(newSettings);
                if (pushError) {
                    setWebPushError(pushError);
                    return;
                }
                setWebPushError(null);
            }
            else {
                delete newSettings.messagePreferences.webPushNotification;
            }
            if (hasValidUserId(initialUser)) {
                await saveUserPreferences(newSettings);
            }
            else {
                finalizeSettings(newSettings);
            }
        }
        finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    }, [
        validateAndGetCleanedName,
        buildNewSettings,
        enabledChannels,
        applyWebPushPreference,
        initialUser,
        saveUserPreferences,
        finalizeSettings,
        phoneNumberError
    ]);
    const renderChannelToggle = useCallback((name, active, handleToggler, disabled) => {
        return renderMessageTypeToggle(name, active, handleToggler, hasUpdatePermission, buttonColor, disabled);
    }, [hasUpdatePermission, buttonColor]);
    const toggleMessagePreference = useCallback((key) => {
        if (!hasUpdatePermission)
            return;
        const newValue = !messagePreferences[key];
        setMessagePreferences((prev) => ({ ...prev, [key]: newValue }));
        // If toggling SMS, re-validate phone number with new required state
        if (key === 'sms') {
            setPhoneNumberError(validatePhoneUtil(phoneNumber, selectedCountry, newValue));
        }
    }, [
        hasUpdatePermission,
        messagePreferences,
        phoneNumber,
        selectedCountry
    ]);
    const toggleInBrowser = useCallback(() => {
        if (!hasUpdatePermission)
            return;
        const turningOn = !messagePreferences.webPushNotification;
        if (!turningOn) {
            // Turning OFF — always allow, clear any lingering error
            setWebPushError(null);
            setMessagePreferences((prev) => ({
                ...prev,
                webPushNotification: false
            }));
            return;
        }
        if (!hasVapidKey) {
            setWebPushError(constants.settings_push_not_configured);
            setMessagePreferences((prev) => ({
                ...prev,
                webPushNotification: true
            }));
            return;
        }
        if (notificationPermission === 'denied') {
            setWebPushError(constants.settings_push_permission_denied);
            setMessagePreferences((prev) => ({
                ...prev,
                webPushNotification: true
            }));
            return;
        }
        setWebPushError(null);
        setMessagePreferences((prev) => ({
            ...prev,
            webPushNotification: true
        }));
    }, [
        hasUpdatePermission,
        hasVapidKey,
        messagePreferences.webPushNotification,
        notificationPermission
    ]);
    return (_jsx(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                borderRadius: '24px',
                maxWidth: '800px',
                width: '744px',
                // Never exceed viewport height;
                maxHeight: 'min(700px, calc(100dvh - 32px))',
                height: 'auto',
                margin: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '@media (max-width: 768px)': {
                    maxWidth: '95vw',
                    maxHeight: 'calc(100dvh - 16px)',
                    margin: '8px'
                },
                '@media (max-width: 1366px)': { maxWidth: '520px' },
                '& > div': {
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: '10px' },
                    '&::-webkit-scrollbar-button': { display: 'none' }
                }
            }
        }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }, children: [_jsxs(Box, { sx: {
                        display: 'flex',
                        padding: '12px 24px',
                        alignItems: 'center',
                        gap: '12px',
                        '@media (max-width: 768px)': { padding: '12px 16px' }
                    }, children: [_jsx(Box, { component: "img", src: SettingsIcon, alt: constants.settings_icon_alt, sx: { width: 24, height: 24, filter: 'brightness(0)' } }), _jsx(Box, { component: "h2", sx: {
                                ...headingStyle,
                                fontSize: '20px',
                                flex: 1,
                                '@media (max-width: 768px)': { fontSize: '18px' }
                            }, children: constants.settings_title })] }), _jsx(Divider, { sx: { borderColor: COLORS.divider } }), languageLoading ? (_jsx(SettingsLoadingView, { buttonColor: configState?.settings?.button_color ||
                        COLORS.primary })) : (_jsxs(Box, { sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        padding: '24px',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': { width: '10px' },
                        '&::-webkit-scrollbar-button': { display: 'none' },
                        '@media (max-width: 768px)': {
                            padding: '16px',
                            gap: '12px'
                        }
                    }, children: [_jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }, children: [_jsx(Box, { component: "h3", sx: headingStyle, children: constants.settings_preferred_name_heading }), _jsx(Box, { component: "p", sx: descriptionStyle, children: constants.settings_preferred_name_description }), _jsx(TextField, { fullWidth: true, size: "small", value: preferredName, onChange: handlePreferredNameChange, disabled: !hasUpdatePermission, error: !!preferredNameError, helperText: preferredNameError ||
                                        constants.settings_preferred_name_helper, sx: {
                                        '& .MuiOutlinedInput-root': {
                                            padding: '8px 12px',
                                            borderRadius: '4px',
                                            ...inputBaseStyle,
                                            '& fieldset': {
                                                borderColor: COLORS.border
                                            },
                                            '&:hover fieldset': {
                                                borderColor: COLORS.border
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: COLORS.primary,
                                                borderWidth: '1px'
                                            },
                                            '&.Mui-error fieldset': {
                                                borderColor: COLORS.error
                                            }
                                        },
                                        '& .MuiOutlinedInput-input': { padding: 0 },
                                        '& .MuiFormHelperText-root': {
                                            fontFamily: 'Open Sans',
                                            fontSize: '12px',
                                            lineHeight: '128%',
                                            marginLeft: 0,
                                            marginTop: '4px'
                                        }
                                    } })] }), _jsx(Divider, { sx: { borderColor: COLORS.divider } }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }, children: [_jsx(Box, { component: "h3", sx: headingStyle, children: constants.settings_tab_roles }), rolesLoading && _jsx(CircularProgress, { size: 16 }), !rolesLoading && userRoles.length > 0 && (_jsx(Box, { sx: {
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '6px'
                                    }, children: userRoles.map((role) => (_jsx(Chip, { label: role.role_name, size: "small", sx: {
                                            backgroundColor: '#f0f0f0',
                                            color: '#595959',
                                            fontFamily: 'Open Sans',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            height: 24
                                        } }, role.role_id))) })), !rolesLoading && userRoles.length === 0 && (_jsx(Typography, { variant: "body2", sx: { fontSize: '14px', color: '#888' }, children: constants.settings_roles_no_assigned }))] }), _jsx(Divider, { sx: { borderColor: COLORS.divider } }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }, children: [_jsx(Box, { component: "h3", sx: headingStyle, children: constants.settings_contact_number_heading }), _jsx(Box, { component: "p", sx: descriptionStyle, children: constants.settings_contact_number_description }), _jsx(PhoneInput, { value: phoneNumber, country: selectedCountry, required: Boolean(messagePreferences.sms), disabled: !hasUpdatePermission, error: phoneNumberError, primaryColor: buttonColor, borderColor: COLORS.border, onValueChange: (newValue) => handlePhoneChange(newValue, selectedCountry), onCountryChange: (newCountry) => handlePhoneChange(phoneNumber, newCountry) })] }), _jsx(Divider, { sx: { borderColor: COLORS.divider } }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }, children: [_jsx(Box, { component: "h3", sx: headingStyle, children: constants.settings_language_heading }), _jsx(Box, { component: "p", sx: descriptionStyle, children: constants.settings_language_description }), _jsx(Select, { fullWidth: true, size: "small", value: selectedLanguage, disabled: !hasUpdatePermission, onChange: (e) => {
                                        const lang = (e.target.value || 'en').toLowerCase();
                                        setSelectedLanguage(lang);
                                    }, sx: {
                                        borderRadius: '4px',
                                        ...inputBaseStyle,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLORS.border
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLORS.border
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLORS.primary,
                                            borderWidth: '1px'
                                        },
                                        '& .MuiSelect-select': {
                                            padding: '12px',
                                            height: '20px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }
                                    }, children: languageOptions.map((opt) => (_jsx(MenuItem, { value: opt.code, children: opt.label }, opt.code))) }), languageError && (_jsx(Box, { component: "p", sx: {
                                        ...descriptionStyle,
                                        fontSize: '12px',
                                        color: COLORS.error
                                    }, children: languageError }))] }), _jsx(Divider, { sx: { borderColor: COLORS.divider } }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }, children: [_jsx(Box, { component: "h3", sx: headingStyle, children: constants.settings_audio_heading }), _jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }, children: [_jsx(Box, { component: "span", sx: {
                                                ...descriptionStyle,
                                                color: COLORS.textPrimary
                                            }, children: constants.settings_audio_off }), _jsxs(Box, { onClick: () => hasUpdatePermission && setAudio(!audio), sx: {
                                                position: 'relative',
                                                width: 48,
                                                height: 32,
                                                cursor: hasUpdatePermission
                                                    ? 'pointer'
                                                    : 'not-allowed',
                                                opacity: hasUpdatePermission ? 1 : 0.5
                                            }, children: [_jsx(Box, { sx: {
                                                        position: 'absolute',
                                                        left: 6,
                                                        top: 8,
                                                        width: 36,
                                                        height: 16,
                                                        borderRadius: '10px',
                                                        backgroundColor: audio
                                                            ? (configState?.settings
                                                                ?.button_color ??
                                                                COLORS.primary)
                                                            : '#EBEBEB',
                                                        opacity: audio ? 0.3 : 1,
                                                        transition: 'background-color 300ms',
                                                        pointerEvents: 'none'
                                                    } }), _jsx(Box, { component: "button", role: "switch", "aria-checked": audio, "aria-label": constants.settings_audio_aria_label, sx: {
                                                        position: 'absolute',
                                                        left: audio ? 24 : 7,
                                                        top: 7,
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: '50%',
                                                        backgroundColor: audio
                                                            ? (configState?.settings
                                                                ?.button_color ??
                                                                COLORS.primary)
                                                            : '#D9D9D9',
                                                        boxShadow: '0px 2px 1px rgba(0, 0, 0, 0.15), 0px 0px 2px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.15)',
                                                        transition: 'left 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 300ms',
                                                        cursor: 'pointer',
                                                        border: 'none',
                                                        padding: 0,
                                                        pointerEvents: 'none',
                                                        '&:focus': {
                                                            outline: `2px solid ${COLORS.primary}`,
                                                            outlineOffset: '2px'
                                                        }
                                                    } })] }), _jsx(Box, { component: "span", sx: descriptionStyle, children: constants.settings_audio_on })] })] }), !channelsLoading && enabledChannels.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Divider, { sx: { borderColor: COLORS.divider } }), _jsxs(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }, children: [_jsxs(Box, { component: "div", children: [_jsx(Typography, { variant: "h3", sx: headingStyle, children: constants.settings_message_preferences_heading }), _jsx(Typography, { variant: "body1", sx: descriptionStyle, children: constants.settings_message_preferences_description })] }), enabledChannels.map((channel) => {
                                            const translatedLabel = getChannelLabelFromConstants(channel.key, channel.label);
                                            return (_jsx(ChannelToggleItem, { channel: {
                                                    ...channel,
                                                    label: translatedLabel
                                                }, messagePreferences: messagePreferences, notificationPermission: notificationPermission, renderChannelToggle: renderChannelToggle, toggleInBrowser: toggleInBrowser, toggleMessagePreference: toggleMessagePreference, webPushError: channel.key === 'webPushNotification'
                                                    ? webPushError
                                                    : null }, channel.key));
                                        })] })] }))] })), saveError && (_jsx(Box, { sx: {
                        px: '24px',
                        pt: '12px',
                        backgroundColor: COLORS.actionButtonsBackground,
                        borderTop: `1px solid ${COLORS.divider}`
                    }, children: _jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 12px',
                            backgroundColor: '#fdecea',
                            borderRadius: '4px',
                            border: '1px solid #f5c6cb'
                        }, children: [_jsx(ErrorOutlineIcon, { sx: { fontSize: '18px', color: COLORS.error, flexShrink: 0 } }), _jsx(Box, { component: "span", sx: {
                                    fontFamily: 'Open Sans',
                                    fontSize: '14px',
                                    color: COLORS.error,
                                    lineHeight: '20px'
                                }, children: saveError })] }) })), _jsx(SettingsFooter, { onClose: onClose, handleSave: handleSave, languageLoading: languageLoading, preferredNameError: preferredNameError, phoneNumberError: phoneNumberError, isDirty: isDirty, hasUpdatePermission: hasUpdatePermission, buttonColor: configState?.settings?.button_color ?? COLORS.primary, isSaving: isSaving, hasWebPushError: !!webPushError })] }) }));
};
export default SettingsModal;
